import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import type {
  ActivityNode,
  ConfigActivityRow,
} from "@/api/config-server-stages";
import { useConfigActivities } from "@/hooks/useConfigServerStages";
import {
  ActivityExecutionRows,
  type ConfigActivityListProps,
} from "./ConfigActivityList";
import { LoadMore } from "./Paging";
import { StageLoading } from "./StageLoading";
export function ExecutionActivityArea({
  projectId,
  node,
  area,
  total,
  onRows,
  props,
  statuses,
}: {
  projectId: string;
  node: ActivityNode;
  area: string | null;
  total: number;
  onRows: (area: string, rows: ConfigActivityRow[]) => void;
  props: Omit<ConfigActivityListProps, "rows">;
  statuses: ReadonlyMap<
    string,
    { status: string; executed: boolean; last_run_id?: string | null }
  >;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const result = useConfigActivities(
    projectId,
    node,
    { area: area ?? "" },
    open,
  );
  const rows = useMemo(
    () => result.data?.pages.flatMap((page) => page.items) ?? [],
    [result.data],
  );
  useEffect(() => {
    onRows(area ?? "", rows);
  }, [area, rows, onRows]);
  const current = rows.map((row) => ({
    ...row,
    ...statuses.get(row.activity_code),
  }));
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 border-b bg-[#fafbfc] px-5 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span data-testid="execution-area-header">{area ?? t("Others")}</span>
        <span className="ml-auto text-xs">{total}</span>
      </button>
      {open && (
        <>
          {result.isPending ? (
            <StageLoading variant="panel" />
          ) : result.isError && !result.data ? (
            <div role="alert">
              <button onClick={() => void result.refetch()}>
                {t("Retry")}
              </button>
            </div>
          ) : (
            <ActivityExecutionRows {...props} rows={current} />
          )}
          {result.isError && result.data && (
            <div role="alert">
              <button onClick={() => void result.fetchNextPage()}>
                {t("Retry")}
              </button>
            </div>
          )}
          <LoadMore
            hasMore={!!result.hasNextPage}
            loading={result.isFetchingNextPage}
            onLoadMore={() => void result.fetchNextPage()}
          />
        </>
      )}
    </section>
  );
}
